import type { PunchResult } from '@worklogix/shared';

/**
 * Durable, offline-first punch queue backed by IndexedDB.
 *
 * The PUNCH is the durable, idempotent, client-timestamped event (plan-v6). Each
 * punch is written here the instant the operator confirms a PIN — before any
 * network — so the clock is fully usable offline and no punch is ever lost to a
 * dropped connection or a reload. The sync engine drains this store.
 *
 * PIN residency is minimized: the PIN is stored transiently only until the punch
 * syncs, then cleared (`pin = null`). PINs are NEVER verified on-device — the
 * server is the sole authority (plan-v5/§B). We keep no plaintext PIN longer than
 * a punch remains unsynced.
 */

const DB_NAME = 'worklogix-kiosk';
/**
 * Bumped 1 → 2 for Phase 2 (plan-v7): adds a SEPARATE `photos` object store for
 * verification-photo blobs (see `PHOTOS_STORE` / `device/photoQueue.ts`). The
 * upgrade is additive and idempotent — it only `createObjectStore`s stores that
 * don't exist, so existing v1 queued punches are preserved across the migration.
 */
const DB_VERSION = 2;
const STORE = 'punches';

/**
 * Name of the Phase 2 photo store. Kept SEPARATE from the punch store so large
 * blobs never slow the hot punch-queue scan (`getPending`). Owned/read/written by
 * `device/photoQueue.ts`, which shares this DB via {@link openKioskDb}.
 */
export const PHOTOS_STORE = 'photos';

export type QueueSyncState = 'pending' | 'synced' | 'error';

export interface QueuedPunch {
  /** Stable client-generated idempotency key (crypto.randomUUID). Primary key. */
  idempotencyKey: string;
  /** Transient PIN, cleared to null once the punch syncs. */
  pin: string | null;
  /** Department this device is bound to (for local projection); may be null offline. */
  departmentId: string | null;
  /** Client wall-clock time of the punch (ISO). Drives payroll — never server now(). */
  punchedAt: string;
  syncState: QueueSyncState;
  /** Sync attempt count, for backoff and give-up detection. */
  attempts: number;
  createdAt: string;
  /**
   * Learned from the authoritative server result once synced. The employee is
   * only known AFTER the server resolves the PIN — there is no client-side
   * attribution (hence no employeeId here).
   */
  employeeName: string | null;
  kind: PunchResult['kind'] | null;
  status: 'applied' | 'duplicate' | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to any queue mutation (enqueue/update). Returns an unsubscribe fn. */
export function subscribeQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open (and, on first use, migrate) the single shared kiosk IndexedDB. Exported
 * so `device/photoQueue.ts` reuses the SAME connection + migration rather than
 * opening a second database. The upgrade creates every store additively, so a
 * v1 → v2 migration never drops the existing `punches` data.
 */
export function openKioskDb(): Promise<IDBDatabase> {
  if (dbPromise !== null) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'idempotencyKey' });
      }
      if (!db.objectStoreNames.contains(PHOTOS_STORE)) {
        db.createObjectStore(PHOTOS_STORE, { keyPath: 'idempotencyKey' });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open kiosk IndexedDB.'));
    };
  });
  return dbPromise;
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Shared IndexedDB request → Promise adapter (reused by `device/photoQueue.ts`). */
export function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed.'));
    };
  });
}

/** Persist a brand-new pending punch. */
export async function enqueue(punch: QueuedPunch): Promise<void> {
  const db = await openKioskDb();
  await promisify(tx(db, 'readwrite').put(punch));
  emit();
}

/** All punches, unordered. */
export async function getAll(): Promise<QueuedPunch[]> {
  const db = await openKioskDb();
  const all = await promisify(tx(db, 'readonly').getAll() as IDBRequest<QueuedPunch[]>);
  return all;
}

/** Pending punches in send order: by client punch time, then insertion time. */
export async function getPending(): Promise<QueuedPunch[]> {
  const all = await getAll();
  return all
    .filter((p) => p.syncState === 'pending')
    .sort((a, b) => {
      if (a.punchedAt !== b.punchedAt) {
        return a.punchedAt < b.punchedAt ? -1 : 1;
      }
      return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
    });
}

/** Apply a partial update to a stored punch by key. */
export async function update(idempotencyKey: string, patch: Partial<QueuedPunch>): Promise<void> {
  const db = await openKioskDb();
  const store = tx(db, 'readwrite');
  const existing = await promisify(
    store.get(idempotencyKey) as IDBRequest<QueuedPunch | undefined>
  );
  if (existing === undefined) {
    return;
  }
  await promisify(store.put({ ...existing, ...patch }));
  emit();
}

/** Count of not-yet-synced punches (for the queued-count badge). */
export async function countPending(): Promise<number> {
  const pending = await getPending();
  return pending.length;
}
